
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  AppBar,
  Toolbar,
  Avatar,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";

import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import MeetingRoomOutlinedIcon from "@mui/icons-material/MeetingRoomOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import "./AdminDashboard.css";

const modules = [
  {
    title: "Student Management",
    description: "Manage student accounts and registrations.",
    icon: <PeopleAltOutlinedIcon />,
    path: "/student-erp/admin/students",
  },
  {
    title: "Departments",
    description: "Organize departments and academic programs.",
    icon: <ApartmentOutlinedIcon />,
    path: "/student-erp/admin/departments",
  },
  {
    title: "Fee Management",
    description: "Manage fee demands, payments and receipts.",
    icon: <AccountBalanceWalletOutlinedIcon />,
    path: "/student-erp/admin/fees",
  },
  {
    title: "Attendance",
    description: "Review attendance records and reports.",
    icon: <FactCheckOutlinedIcon />,
    path: "/student-erp/admin/attendance",
  },
  {
    title: "Library",
    description: "Manage library records and transactions.",
    icon: <MenuBookOutlinedIcon />,
    path: "/student-erp/admin/library",
  },
  {
    title: "Hostel Management",
    description: "Manage hostel allocations and payments.",
    icon: <MeetingRoomOutlinedIcon />,
    path: "/student-erp/admin/hostel",
  },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);

  useEffect(() => {
    const token = sessionStorage.getItem("erpToken");
    const storedUser = sessionStorage.getItem("erpUser");

    if (!token || !storedUser) {
      navigate("/student-erp/login?role=ADMIN", { replace: true });
      return;
    }

    try {
      const parsedUser = JSON.parse(storedUser);

      if (parsedUser.role !== "ADMIN") {
        navigate("/student-erp", { replace: true });
        return;
      }

      setUser(parsedUser);
    } catch {
      sessionStorage.removeItem("erpToken");
      sessionStorage.removeItem("erpUser");
      navigate("/student-erp/login?role=ADMIN", { replace: true });
    }
  }, [navigate]);

  const handleLogout = () => {
    sessionStorage.removeItem("erpToken");
    sessionStorage.removeItem("erpUser");
    navigate("/student-erp/login?role=ADMIN", { replace: true });
  };

  if (!user) return null;

  return (
    <Box className="erp-admin-page">
      <AppBar position="static" className="erp-admin-appbar" elevation={0}>
        <Toolbar className="erp-admin-toolbar">
          <Box className="erp-admin-brand">
            <Avatar className="erp-admin-brand-icon">
              <DashboardOutlinedIcon />
            </Avatar>
            <Box>
              <Typography className="erp-admin-brand-title">
                RAYALASEEMA UNIVERSITY
              </Typography>
              <Typography className="erp-admin-brand-subtitle">
                Student ERP Administration
              </Typography>
            </Box>
          </Box>

          <Box className="erp-admin-user">
            <Box className="erp-admin-user-details">
              <Typography className="erp-admin-user-name">
                {user.fullName}
              </Typography>
              <Typography className="erp-admin-user-role">
                University Administrator
              </Typography>
            </Box>

            <Button
              color="inherit"
              startIcon={<LogoutOutlinedIcon />}
              onClick={handleLogout}
            >
              Logout
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" className="erp-admin-content">
        <Box className="erp-admin-welcome">
          <Typography variant="h4" component="h1">
            Administration Dashboard
          </Typography>
          <Typography>
            Manage university academic and administrative services from one
            place.
          </Typography>
        </Box>

        <Grid container spacing={2.5} className="erp-admin-summary">
          <Grid item xs={12} sm={6} md={4}>
            <Paper className="erp-admin-summary-card" elevation={0}>
              <Avatar className="erp-summary-icon">
                <PeopleAltOutlinedIcon />
              </Avatar>
              <Typography className="erp-summary-label">
                Student Management
              </Typography>
              <Typography className="erp-summary-note">
                Student records and registration
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Paper className="erp-admin-summary-card" elevation={0}>
              <Avatar className="erp-summary-icon">
                <AccountBalanceWalletOutlinedIcon />
              </Avatar>
              <Typography className="erp-summary-label">
                Finance
              </Typography>
              <Typography className="erp-summary-note">
                Fees, payments and receipts
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Paper className="erp-admin-summary-card" elevation={0}>
              <Avatar className="erp-summary-icon">
                <FactCheckOutlinedIcon />
              </Avatar>
              <Typography className="erp-summary-label">
                Academic Records
              </Typography>
              <Typography className="erp-summary-note">
                Attendance and academic administration
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Box className="erp-admin-section-heading">
          <Typography variant="h5" component="h2">
            ERP Modules
          </Typography>
          <Typography>
            Select a module to continue.
          </Typography>
        </Box>

        <Grid container spacing={2.5}>
          {modules.map((module) => (
            <Grid item xs={12} sm={6} lg={4} key={module.title}>
              <Paper className="erp-admin-module-card" elevation={0}>
                <Avatar className="erp-admin-module-icon">
                  {module.icon}
                </Avatar>

                <Typography className="erp-admin-module-title">
                  {module.title}
                </Typography>

                <Typography className="erp-admin-module-description">
                  {module.description}
                </Typography>

                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => navigate(module.path)}
                >
                  Open Module
                </Button>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Divider className="erp-admin-divider" />

        <Typography className="erp-admin-footer">
          © {new Date().getFullYear()} Rayalaseema University, Kurnool.
          Student ERP Portal.
        </Typography>
      </Container>
    </Box>
  );
}
